import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  deleteUser,
  updatePassword,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
  reauthenticateWithCredential,
  EmailAuthProvider,
  onAuthStateChanged,
  signInWithCustomToken
} from "firebase/auth";
import {
  getDoc, deleteDoc, updateDoc, collection, getDocs, doc, setDoc,
  Timestamp, where, query, addDoc, serverTimestamp, onSnapshot, getCountFromServer
} from "firebase/firestore";
import { auth, db } from "../../src/firebase";
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { AppDispatch, RootState } from "./store";

// ── Deployed username generator URL ──
const USERNAME_API = "https://europe-west1-eartlas.cloudfunctions.net/generateUniqueUsername";

// ── Helper: call backend to generate or check username ──
async function resolveUsername(
  opts:
    | { mode: "generate"; firstName: string; lastName: string }
    | { mode: "check"; username: string }
): Promise<{ username: string; available: boolean }> {
  const body =
    opts.mode === "generate"
      ? { firstName: opts.firstName, lastName: opts.lastName }
      : { existingUsername: opts.username };

  const res  = await fetch(USERNAME_API, {
    method:  "POST",
    headers: { "Content-Type": "application/json" },
    body:    JSON.stringify(body),
  });
  return res.json();
}

export interface UserData {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  role?: string[];
  phoneNumber?: string;
  profilePicture?: string;
  joinDate: string;
  emailAddress?: string;
  username?: string;
  userBio?: string;
  userAge?: number;
  verifyEmail?: boolean;
  verifyIdentity?: boolean;
  verifyPhone?: boolean;
  isAdmin?: boolean;
  isSupport?: boolean;
  termsAccepted?: boolean;
  address?: {
    streetAddress?: string;
    postalAddress?: string;
    city?: string;
    state?: string;
    country?: string;
    countryCode?: string;
    zipCode?: string;
    apartmentNumber?: string;
  };
  shippingAddress?: {
    streetAddress?: string;
    apartmentNumber?: string;
    postalAddress?: string;
    city?: string;
    state?: string;
    country?: string;
    countryCode?: string;
    zipCode?: string;
  };
  [key: string]: any;
}

export interface ShippingAddress {
  streetAddress?: string;
  apartmentNumber?: string;
  postalAddress?: string;
  city?: string;
  state?: string;
  country?: string;
  countryCode?: string;
  zipCode?: string;
}

interface AuthState {
  user: UserData | null;
  allUsers: UserData[];
  supportId: string | null;
  totalUsers: number;
  status: "idle" | "loading" | "failed";
  error: string | null;
  authLoaded: boolean;
  managers: string[] | null;
}

const initialState: AuthState = {
  user: null,
  allUsers: [],
  status: "idle",
  error: null,
  authLoaded: false,
  supportId: null,
  totalUsers: 0,
  managers: null,
};

interface ReferralPayload {
  username: string;
  email: string;
  referralCode: string;
  referredBy: string;
  isReferred: boolean;
  createdAt?: string;
}

// ─────────────────────────────────────────────────────────────
// GOOGLE LOGIN
// New users get an auto-generated username from their name
// ─────────────────────────────────────────────────────────────
export const loginWithGoogle = createAsyncThunk(
  "auth/loginWithGoogle",
  async (_, { rejectWithValue }) => {
    try {
      const provider = new GoogleAuthProvider();
      const result   = await signInWithPopup(auth, provider);
      const user     = result.user;
      const uid      = user.uid;
      const normalizedEmail = user.email?.trim().toLowerCase();

      if (!normalizedEmail) {
        throw new Error("Google account is missing an email address.");
      }

      // Check if user already exists in Firestore
      const usersRef   = collection(db, "users");
      const emailQuery = query(usersRef, where("email", "==", normalizedEmail));
      const emailSnap  = await getDocs(emailQuery);

      if (!emailSnap.empty) {
        const existing = emailSnap.docs[0].data() as UserData;

        if (existing.provider === "password") {
          throw new Error(
            "This email is already registered with email/password. Please log in using email instead."
          );
        } else if (existing.provider !== "google") {
          throw new Error(
            "This email is already registered with another provider. Please use that to log in."
          );
        }

        // Existing Google user — return as-is
        return existing;
      }

      // ── New Google user — auto-generate username from display name ──
      const [firstName = "", lastName = ""] = user.displayName?.split(" ") ?? [];

      const { username: generatedUsername } = await resolveUsername({
        mode: "generate",
        firstName,
        lastName,
      });

      const referralLink = `https://saloisa.com/referral/?referral-code=${encodeURIComponent(generatedUsername)}`;

      const userDoc: UserData = {
        userId:         uid,
        email:          normalizedEmail,
        emailAddress:   normalizedEmail,
        firstName,
        lastName,
        phoneNumber:    user.phoneNumber ?? "",
        profilePicture: user.photoURL    ?? "",
        username:       generatedUsername,
        referralLink,
        termsAccepted:  false,
        joinDate:       new Date().toISOString(),
        provider:       "google",
      };

      await setDoc(doc(db, "users", uid), userDoc);
      console.log(`✅ New Google user created: ${normalizedEmail} → @${generatedUsername}`);
      return userDoc;

    } catch (err: any) {
      console.error("❌ Google login error:", err.message);
      return rejectWithValue(err.message);
    }
  }
);

// ─────────────────────────────────────────────────────────────
// EMAIL REGISTRATION
// If user provides a username → check availability via backend
// If user leaves it blank    → generate from firstName+lastName
// ─────────────────────────────────────────────────────────────
export const registerWithEmail = createAsyncThunk(
  "auth/registerWithEmail",
  async (
    {
      email,
      password,
      firstName,
      lastName,
      phoneNumber,
      profilePicture,
      username,
    }: Omit<UserData, "userId" | "joinDate"> & { password: string; username?: string },
    { rejectWithValue }
  ) => {
    try {
      const normalizedEmail = email.trim().toLowerCase();
      const usersRef        = collection(db, "users");

      // ── Check email duplicates ──
      const emailQuery = query(usersRef, where("email", "in", [normalizedEmail, email]));
      const emailSnap  = await getDocs(emailQuery);

      let existingUser: UserData | null = null;
      if (!emailSnap.empty) {
        existingUser = emailSnap.docs[0].data() as UserData;
      } else {
        const emailAddrQuery = query(usersRef, where("emailAddress", "==", normalizedEmail));
        const emailAddrSnap  = await getDocs(emailAddrQuery);
        if (!emailAddrSnap.empty) {
          existingUser = emailAddrSnap.docs[0].data() as UserData;
        }
      }

      if (existingUser) {
        if (existingUser.provider === "google") {
          throw new Error("This email is already registered with Google. Please log in using Google.");
        } else if (existingUser.provider === "password") {
          throw new Error("This email is already registered.");
        } else {
          throw new Error("This email is already linked to another provider.");
        }
      }

      // ── Resolve username via backend ──
      const providedUsername = username?.trim() || "";

      const usernameData = await resolveUsername(
        providedUsername
          ? { mode: "check",    username:  providedUsername }
          : { mode: "generate", firstName: firstName as string, lastName: lastName as string }
      );

      if (providedUsername && !usernameData.available) {
        throw new Error("This username is already taken. Please choose another.");
      }

      const finalUsername = usernameData.username;

      // ── Create Firebase Auth user ──
      const userCred = await createUserWithEmailAndPassword(auth, normalizedEmail, password);
      const uid      = userCred.user.uid;

      const referralLink = `https://saloisa.com/referral/?referral-code=${encodeURIComponent(finalUsername)}`;

      const userDoc: UserData = {
        userId:        uid,
        email:         normalizedEmail,
        emailAddress:  normalizedEmail,
        firstName:     firstName as string,
        lastName:      lastName  as string,
        phoneNumber:   phoneNumber  as string,
        profilePicture: profilePicture as string,
        username:      finalUsername,
        referralLink,
        termsAccepted: false,
        joinDate:      new Date().toISOString(),
        provider:      "password",
      };

      await setDoc(doc(db, "users", uid), userDoc);
      console.log(`✅ User registered: ${normalizedEmail} → @${finalUsername}`);
      return userDoc;

    } catch (err: any) {
      console.error("❌ Registration error:", err.message);
      return rejectWithValue(err.message);
    }
  }
);

// ─────────────────────────────────────────────────────────────
// EMAIL LOGIN
// ─────────────────────────────────────────────────────────────
export const loginWithEmail = createAsyncThunk(
  "auth/loginWithEmail",
  async ({ email, password }: { email: string; password: string }) => {
    const userCred = await signInWithEmailAndPassword(auth, email, password);
    const snapshot = await getDoc(doc(db, "users", userCred.user.uid));
    return snapshot.data() as UserData;
  }
);

export const signInWithCustomTokenThunk = createAsyncThunk(
  "auth/signInWithCustomToken",
  async (token: string, { rejectWithValue }) => {
    try {
      const result  = await signInWithCustomToken(auth, token);
      const uid     = result.user.uid;
      const docSnap = await getDoc(doc(db, "users", uid));
      if (!docSnap.exists()) throw new Error("User not found in Firestore");
      return docSnap.data() as UserData;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const logout = createAsyncThunk<void, void, { state: RootState }>(
  "auth/logout",
  async (_, { getState }) => {
    const state = getState();
    const user  = state.auth.user;
    if (user) {
      console.log(`Logging out user: ${user.email} (ID: ${user.userId})`);
    }
    await signOut(auth);
  }
);

export const deleteAccount = createAsyncThunk(
  "auth/deleteAccount",
  async (_, { rejectWithValue }) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("No authenticated user");

      const uid     = currentUser.uid;
      const userRef = doc(db, "users", uid);
      const snap    = await getDoc(userRef);
      const storage = getStorage();

      if (snap.exists()) {
        const data = snap.data();
        if (data?.profilePicture) {
          try { await deleteObject(ref(storage, data.profilePicture)); } catch {}
        }
        try { await deleteObject(ref(storage, `profilePictures/${uid}.jpg`)); } catch {}
        await deleteDoc(userRef);
      }

      await currentUser.delete();
      return uid;

    } catch (err: any) {
      console.error("🔥 deleteAccount error:", err);
      return rejectWithValue(err.message);
    }
  }
);

export const deactivateAccount = createAsyncThunk(
  "auth/deactivateAccount",
  async (_, { rejectWithValue }) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("Not authenticated");
      await updateDoc(doc(db, "users", currentUser.uid), {
        accountStatus: "deactivated",
        deactivatedAt: serverTimestamp(),
      });
      return currentUser.uid;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const reactivateAccount = createAsyncThunk(
  "auth/reactivateAccount",
  async (_, { rejectWithValue }) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("Not authenticated");
      await updateDoc(doc(db, "users", currentUser.uid), {
        accountStatus: "active",
        reactivatedAt: serverTimestamp(),
      });
      return currentUser.uid;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchUserData = createAsyncThunk(
  "auth/fetchUserData",
  async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("Not logged in");

    const userRef = doc(db, "users", currentUser.uid);
    const docSnap = await getDoc(userRef);
    if (!docSnap.exists()) throw new Error("User document not found");

    const data = docSnap.data();

    const reviewedAt =
      data?.reviewedAt instanceof Timestamp
        ? data.reviewedAt.toDate().toISOString()
        : data?.reviewedAt ?? null;

    const address = {
      streetAddress:   data?.address?.streetAddress   || "",
      postalAddress:   data?.address?.postalAddress   || "",
      city:            data?.address?.city            || "",
      state:           data?.address?.state           || "",
      country:         data?.address?.country         || "",
      countryCode:     data?.address?.countryCode     || "",
      zipCode:         data?.address?.zipCode         || "",
      apartmentNumber: data?.address?.apartmentNumber || "",
    };

    const shippingAddress = {
      streetAddress:   data?.shippingAddress?.streetAddress   || "",
      postalAddress:   data?.shippingAddress?.postalAddress   || "",
      city:            data?.shippingAddress?.city            || "",
      state:           data?.shippingAddress?.state           || "",
      country:         data?.shippingAddress?.country         || "",
      countryCode:     data?.shippingAddress?.countryCode     || "",
      zipCode:         data?.shippingAddress?.zipCode         || "",
      apartmentNumber: data?.shippingAddress?.apartmentNumber || "",
    };

    return {
      ...(data as UserData),
      reviewedAt,
      address,
      shippingAddress,
    };
  }
);

export const sendResetEmail = createAsyncThunk(
  "auth/sendResetEmail",
  async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  }
);

export const updateUserProfile = createAsyncThunk(
  "auth/updateUserProfile",
  async (updatedData: Partial<UserData>) => {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("Not logged in");
    const userRef = doc(db, "users", currentUser.uid);
    await setDoc(userRef, updatedData, { merge: true });
    const updatedDoc = await getDoc(userRef);
    return updatedDoc.data() as UserData;
  }
);

export const uploadProfilePicture = createAsyncThunk(
  "auth/uploadProfilePicture",
  async ({ file }: { file: File }) => {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("Not logged in");

    const storage    = getStorage();
    const storageRef = ref(storage, `profilePictures/${currentUser.uid}/${file.name}`);
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);

    const userRef = doc(db, "users", currentUser.uid);
    await setDoc(userRef, { profilePicture: downloadURL }, { merge: true });
    const updatedDoc = await getDoc(userRef);
    return updatedDoc.data() as UserData;
  }
);

export const uploadLivenessVideo = createAsyncThunk(
  "auth/uploadLivenessVideo",
  async (file: File, { rejectWithValue }) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("Not logged in");

      const storage    = getStorage();
      const storageRef = ref(storage, `identityVideo/${currentUser.uid}/liveness-${Date.now()}.webm`);
      await uploadBytes(storageRef, file);
      const videoUrl = await getDownloadURL(storageRef);

      const userRef = doc(db, "users", currentUser.uid);
      await setDoc(userRef, { videoUrl, verifyVideo: true }, { merge: true });
      const updatedDoc = await getDoc(userRef);
      return updatedDoc.data() as UserData;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const uploadIdentityDocument = createAsyncThunk(
  "auth/uploadIdentityDocument",
  async ({ file, side }: { file: File; side: "front" | "back" }, { rejectWithValue }) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error("Not logged in");

      const storage    = getStorage();
      const storageRef = ref(storage, `identityDocs/${currentUser.uid}/id-${side}-${Date.now()}.jpg`);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      const update: Partial<UserData> = { verifyDocument: true };
      if (side === "front") update.idFrontUrl = downloadURL;
      if (side === "back")  update.idBackUrl  = downloadURL;

      const userRef = doc(db, "users", currentUser.uid);
      await setDoc(userRef, update, { merge: true });
      const updatedDoc = await getDoc(userRef);
      return updatedDoc.data() as UserData;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const sendEmailVerificationCode = createAsyncThunk(
  "auth/sendEmailVerificationCode",
  async ({ email }: { email: string }, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { auth: AuthState };
      const user  = state.auth.user;
      if (!user?.userId) throw new Error("Missing user ID");
      if (!email)        throw new Error("Missing email");

      const response = await fetch(process.env.REACT_APP_EMAIL_VERIFICATION_API!, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ userId: user.userId, email }),
      });

      if (!response.ok) {
        const error = await response.text();
        throw new Error(error);
      }
      return true;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const confirmEmailVerificationCode = createAsyncThunk(
  "auth/confirmEmailVerificationCode",
  async ({ inputCode }: { inputCode: string }, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState() as { auth: AuthState };
      const user  = state.auth.user;
      if (!user) throw new Error("User not found");

      const userRef  = doc(db, "users", user.userId);
      const snapshot = await getDoc(userRef);
      const data     = snapshot.data();

      if (snapshot.exists() && data?.verificationCode === inputCode.trim()) {
        await updateDoc(userRef, { verifyEmail: true, verificationCode: "" });
        dispatch(updateUserProfile({ verifyEmail: true }));
        return true;
      } else {
        throw new Error("Verification code is incorrect");
      }
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const changePassword = createAsyncThunk(
  "auth/changePassword",
  async ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) => {
    const user = auth.currentUser;
    if (!user?.email) throw new Error("User not logged in or missing email");
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    await updatePassword(user, newPassword);
  }
);

export const listenForAuthChanges = createAsyncThunk(
  "auth/listenForAuthChanges",
  async (_, { dispatch }) => {
    return new Promise<UserData | null>((resolve) => {
      onAuthStateChanged(auth, async (firebaseUser) => {
        if (firebaseUser) {
          const snapshot = await getDoc(doc(db, "users", firebaseUser.uid));
          const userData = snapshot.data() as UserData;
          if (!userData.userId) userData.userId = firebaseUser.uid;
          resolve(userData);
        } else {
          resolve(null);
        }
      });
    });
  }
);

export const fetchAllUsers = createAsyncThunk<UserData[], void, { rejectValue: string }>(
  "auth/fetchAllUsers",
  async (_, { rejectWithValue }) => {
    try {
      const querySnapshot = await getDocs(collection(db, "users"));
      const users: UserData[] = querySnapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        const reviewedAt =
          data.reviewedAt instanceof Timestamp
            ? data.reviewedAt.toDate().toISOString()
            : data.reviewedAt ?? null;
        return { ...(data as UserData), userId: docSnap.id, reviewedAt };
      });
      return users;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const getSupportId = createAsyncThunk<UserData | null, void, { rejectValue: string }>(
  "auth/getSupportId",
  async (_, { rejectWithValue }) => {
    try {
      const querySnapshot = await getDocs(collection(db, "users"));
      const supportUser = querySnapshot.docs
        .map((doc) => ({ ...(doc.data() as UserData), userId: doc.id }))
        .find((user) => user.role?.includes("support"));
      return supportUser || null;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const generateReferralLink = createAsyncThunk(
  "auth/generateReferralLink",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { auth: AuthState };
      const user  = state.auth.user;
      if (!user || !user.username) throw new Error("Username missing — cannot create referral link.");

      const referralLink = `https://areluxe.com/referral/?referral-code=${encodeURIComponent(user.username)}`;
      const userRef      = doc(db, "users", user.userId);
      await updateDoc(userRef, { referralLink });
      return { ...user, referralLink };
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const listenToReferrerId = createAsyncThunk(
  "auth/listenToReferrerId",
  async (
    { referralCode, username, email, onUpdate }: {
      referralCode: string;
      username?: string;
      email?: string;
      onUpdate: (data: { referrerId: string | null; userExists: boolean }) => void;
    },
    { rejectWithValue }
  ) => {
    try {
      if (!referralCode) throw new Error("Missing referral code");

      const referrerQ = query(collection(db, "users"), where("username", "==", referralCode.trim()));

      const unsubscribeReferrer = onSnapshot(referrerQ, async (snap) => {
        let referrerId: string | null = null;
        let userExists = false;

        if (!snap.empty) referrerId = snap.docs[0].id;

        if (username || email) {
          const usersRef   = collection(db, "users");
          const usernameQ  = query(usersRef, where("username",     "==", username || ""));
          const emailQ     = query(usersRef, where("emailAddress", "==", email    || ""));

          const unsubUsername = onSnapshot(usernameQ, (snap2) => {
            userExists = !snap2.empty;
            onUpdate({ referrerId, userExists });
          });
          const unsubEmail = onSnapshot(emailQ, (snap3) => {
            userExists = userExists || !snap3.empty;
            onUpdate({ referrerId, userExists });
          });

          return () => { unsubUsername(); unsubEmail(); };
        } else {
          onUpdate({ referrerId, userExists });
        }
      });

      return () => unsubscribeReferrer();
    } catch (error: any) {
      console.error("❌ Error listening to referrer:", error);
      return rejectWithValue(error.message);
    }
  }
);

export const upsertRefferrals = createAsyncThunk(
  "auth/upsertRefferrals",
  async (data: ReferralPayload, { rejectWithValue }) => {
    try {
      const referralRef = collection(db, "referrals");
      const usersRef    = collection(db, "users");

      const q        = query(referralRef, where("username", "==", data.username));
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        await updateDoc(snapshot.docs[0].ref, {
          ...data, isRegistered: false, hasListings: false, hasBookings: false,
          updatedAt: serverTimestamp(),
        });
      } else {
        await addDoc(referralRef, {
          ...data, isRegistered: false, hasListings: false, hasBookings: false,
          createdAt: data.createdAt || serverTimestamp(),
        });
      }

      const userQuery = query(usersRef, where("emailAddress", "==", data.email));
      const userSnap  = await getDocs(userQuery);
      if (!userSnap.empty) {
        await updateDoc(userSnap.docs[0].ref, {
          isRegistered: false, hasListings: false, hasBookings: false,
          isReferred: true, referredBy: data.referredBy, referralCode: data.referralCode,
          updatedAt: serverTimestamp(),
        });
      }

      return { ...data, isRegistered: false, hasListings: false, hasBookings: false };
    } catch (error: any) {
      console.error("Error saving referral:", error);
      return rejectWithValue(error.message);
    }
  }
);

export const listenToReferredUserData = createAsyncThunk(
  "auth/listenToReferredUserData",
  async (
    { username, email, onUpdate }: {
      username?: string;
      email?: string;
      onUpdate: (data: { referralId: string | null; referralData: any | null }) => void;
    },
    { rejectWithValue }
  ) => {
    try {
      if (!username && !email) throw new Error("Missing username or email to listen for referrals.");

      const referralsRef = collection(db, "referrals");
      const q = username
        ? query(referralsRef, where("username", "==", username))
        : query(referralsRef, where("email",    "==", email));

      const unsubscribe = onSnapshot(q, (snapshot) => {
        if (!snapshot.empty) {
          const d = snapshot.docs[0];
          onUpdate({ referralId: d.id, referralData: d.data() });
        } else {
          onUpdate({ referralId: null, referralData: null });
        }
      });

      return () => unsubscribe();
    } catch (error: any) {
      console.error("❌ Error listening to referred user data:", error);
      return rejectWithValue(error.message);
    }
  }
);

export const startInactivityWatcher = createAsyncThunk<
  void,
  { timeoutMs?: number },
  { dispatch: AppDispatch; state: RootState }
>(
  "auth/startInactivityWatcher",
  async ({ timeoutMs = 5 * 60 * 1000 }, { dispatch, getState }) => {
    let timer: ReturnType<typeof setTimeout>;

    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(async () => {
        const { user } = getState().auth;
        if (user) {
          console.warn("⏳ Session expired due to inactivity");
          await dispatch(logout());
          alert("You've been logged out due to inactivity.");
          window.location.href = "/AuthSwitch";
        }
      }, timeoutMs);
    };

    const events = ["mousemove","mousedown","keydown","scroll","touchstart"];
    events.forEach(e => window.addEventListener(e, resetTimer));
    resetTimer();

    window.addEventListener("beforeunload", () => {
      events.forEach(e => window.removeEventListener(e, resetTimer));
      clearTimeout(timer);
    });
  }
);

export const listenToTotalUsers = createAsyncThunk(
  "auth/listenToTotalUsers",
  async (_, { dispatch }) => {
    const unsubscribe = onSnapshot(collection(db, "users"), (snapshot) => {
      dispatch(setTotalUsers(snapshot.size));
    });
    return unsubscribe;
  }
);

export const fetchTotalUsers = createAsyncThunk<number, void, { rejectValue: string }>(
  "auth/fetchTotalUsers",
  async (_, { rejectWithValue }) => {
    try {
      const snapshot = await getCountFromServer(collection(db, "users"));
      return snapshot.data().count;
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : "Failed to fetch total users");
    }
  }
);

export const listenToManagers = () => (dispatch: AppDispatch) => {
  const unsubscribe = onSnapshot(
    collection(db, "managers"),
    (snapshot) => dispatch(setManagers(snapshot.docs.map((doc) => doc.id))),
    (error)    => console.error("[listenToManagers] snapshot error:", error)
  );
  return unsubscribe;
};

// ─────────────────────────────────────────────────────────────
// SLICE
// ─────────────────────────────────────────────────────────────
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearAuthState: (state) => {
      state.user   = null;
      state.status = "idle";
      state.error  = null;
    },
    setTotalUsers: (state, action) => {
      state.totalUsers = action.payload;
    },
    setManagers: (state, action: PayloadAction<string[]>) => {
      state.managers = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(registerWithEmail.fulfilled,       (state, action) => { state.user = action.payload; })
      .addCase(loginWithEmail.fulfilled,          (state, action) => { state.user = action.payload; })
      .addCase(loginWithGoogle.fulfilled,         (state, action) => { state.user = action.payload; })
      .addCase(fetchUserData.fulfilled,           (state, action) => { state.user = action.payload; })
      .addCase(logout.fulfilled,                  (state)         => { state.user = null; })
      .addCase(listenForAuthChanges.fulfilled,    (state, action) => { state.user = action.payload; state.authLoaded = true; })
      .addCase(listenForAuthChanges.rejected,     (state)         => { state.authLoaded = true; })
      .addCase(updateUserProfile.fulfilled,       (state, action) => { state.user = action.payload; })
      .addCase(uploadProfilePicture.fulfilled,    (state, action) => { state.user = action.payload; })
      .addCase(fetchAllUsers.fulfilled,           (state, action) => { state.allUsers = action.payload; })
      .addCase(signInWithCustomTokenThunk.fulfilled, (state, action) => { state.user = action.payload; })
      .addCase(fetchTotalUsers.fulfilled,         (state, action) => { state.totalUsers = action.payload; })
      .addCase(getSupportId.fulfilled,            (state, action) => { state.supportId = action.payload?.userId || null; })
      .addCase(generateReferralLink.fulfilled,    (state, action) => { state.user = action.payload; })
      .addCase(deleteAccount.fulfilled,           (state)         => { state.user = null; })
      .addCase(deactivateAccount.fulfilled,       (state)         => { if (state.user) state.user.accountStatus = "deactivated"; })
      .addCase(reactivateAccount.fulfilled,       (state)         => { if (state.user) state.user.accountStatus = "active"; });
  },
});

export const { clearAuthState, setTotalUsers, setManagers } = authSlice.actions;
export default authSlice.reducer;